/**
 * Pure geometry for photo drawings (gallery F-6). Shapes are stored in NORMALISED 0..1 coordinates
 * of the upright photo; on screen they are drawn in an SVG whose viewBox is the photo's own pixel
 * box (W x H), so text and arrowheads keep their proportions on any photo shape.
 */
import {
  MARKUP_LIMITS,
  type MarkupColor,
  type MarkupPoint,
  type MarkupShape,
  type MarkupTextSize,
  type MarkupWidth,
} from '../../../../types/fieldService/generatorPhotoMarkupVocab';

/** Screen colours for the fixed palette (the server validates the names, not these values). */
export const MARKUP_COLOR_HEX: Record<MarkupColor, string> = {
  red: '#EF4444',
  yellow: '#FACC15',
  green: '#22C55E',
  blue: '#3FA5FF',
  white: '#FFFFFF',
  black: '#111827',
};

/** Line width as a share of the photo's shorter side, so a mark reads the same on any photo. */
const WIDTH_SHARE: Record<MarkupWidth, number> = { thin: 0.004, medium: 0.008, thick: 0.014 };
const TEXT_SHARE: Record<MarkupTextSize, number> = { small: 0.03, medium: 0.045, large: 0.065 };

export function strokePx(width: MarkupWidth, w: number, h: number): number {
  return Math.max(1, WIDTH_SHARE[width] * Math.min(w, h));
}

export function fontPx(size: MarkupTextSize, w: number, h: number): number {
  return Math.max(8, TEXT_SHARE[size] * Math.min(w, h));
}

export const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

/** Minimal matrix shape (DOMMatrix / SVGMatrix) so tests can pass a plain object. */
export interface Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

/**
 * Screen point -> normalised photo point. `inverse` is svg.getScreenCTM().inverse(): it undoes
 * every CSS transform on the way (the viewer's rotate and zoom), so the mapping is exact at any
 * rotation or zoom without resetting the view.
 */
export function screenToNormalised(clientX: number, clientY: number, inverse: Matrix, w: number, h: number): MarkupPoint {
  const x = inverse.a * clientX + inverse.c * clientY + inverse.e;
  const y = inverse.b * clientX + inverse.d * clientY + inverse.f;
  return { x: clamp01(x / w), y: clamp01(y / h) };
}

function perpendicularDistance(p: MarkupPoint, a: MarkupPoint, b: MarkupPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  return Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / len;
}

/** Ramer-Douglas-Peucker, iterative (a long stroke must not blow the stack). */
function rdp(points: MarkupPoint[], epsilon: number): MarkupPoint[] {
  if (points.length < 3) return points;
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop() as [number, number];
    let maxDist = 0;
    let index = -1;
    for (let i = start + 1; i < end; i += 1) {
      const d = perpendicularDistance(points[i], points[start], points[end]);
      if (d > maxDist) {
        maxDist = d;
        index = i;
      }
    }
    if (index !== -1 && maxDist > epsilon) {
      keep[index] = true;
      stack.push([start, index], [index, end]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/**
 * Simplify a freehand stroke so it fits the per-stroke cap: drop near-duplicate points, then
 * RDP with a growing tolerance until it fits. Always keeps the first and last point.
 */
export function simplifyStroke(points: MarkupPoint[], maxPoints: number = MARKUP_LIMITS.maxPointsPerStroke): MarkupPoint[] {
  const deduped = points.filter((p, i) => i === 0 || Math.hypot(p.x - points[i - 1].x, p.y - points[i - 1].y) > 0.0005);
  if (deduped.length === 1 && points.length > 1) deduped.push(points[points.length - 1]);
  let epsilon = 0.0008;
  let out = rdp(deduped, epsilon);
  while (out.length > maxPoints) {
    epsilon *= 1.6;
    out = rdp(deduped, epsilon);
  }
  return out;
}

/** Arrowhead for a line from -> to, in PIXELS of the W x H box. */
export function arrowHeadPath(from: MarkupPoint, to: MarkupPoint, w: number, h: number, stroke: number): string {
  const fx = from.x * w;
  const fy = from.y * h;
  const tx = to.x * w;
  const ty = to.y * h;
  const angle = Math.atan2(ty - fy, tx - fx);
  const size = Math.max(stroke * 4, 10);
  const spread = Math.PI / 7;
  const x1 = tx - size * Math.cos(angle - spread);
  const y1 = ty - size * Math.sin(angle - spread);
  const x2 = tx - size * Math.cos(angle + spread);
  const y2 = ty - size * Math.sin(angle + spread);
  return `M ${x1} ${y1} L ${tx} ${ty} L ${x2} ${y2}`;
}

/** Move a shape by (dx, dy) normalised units, keeping every coordinate inside the photo. */
export function translateShape(shape: MarkupShape, dx: number, dy: number): MarkupShape {
  const p = (pt: MarkupPoint): MarkupPoint => ({ x: clamp01(pt.x + dx), y: clamp01(pt.y + dy) });
  switch (shape.type) {
    case 'arrow':
      return { ...shape, from: p(shape.from), to: p(shape.to) };
    case 'ellipse':
      return { ...shape, cx: clamp01(shape.cx + dx), cy: clamp01(shape.cy + dy) };
    case 'freehand':
      return { ...shape, points: shape.points.map(p) };
    case 'text':
      return { ...shape, at: p(shape.at) };
    case 'textBox': {
      const x = Math.min(Math.max(0, shape.x + dx), 1 - shape.w);
      const y = Math.min(Math.max(0, shape.y + dy), 1 - shape.h);
      return { ...shape, x, y };
    }
  }
}

/** Freehand points across all shapes (the server's total cap). */
export function totalPoints(shapes: MarkupShape[]): number {
  return shapes.reduce((n, s) => n + (s.type === 'freehand' ? s.points.length : 0), 0);
}

/** A plain-language description of a drawing, for screen readers. */
export function describeDrawing(shapes: MarkupShape[]): string {
  if (shapes.length === 0) return 'No drawing';
  const labels = shapes
    .filter((s): s is Extract<MarkupShape, { text: string }> => s.type === 'text' || s.type === 'textBox')
    .map((s) => `"${s.text}"`);
  const count = `${shapes.length} mark${shapes.length === 1 ? '' : 's'}`;
  return labels.length ? `Drawing with ${count}: ${labels.join(', ')}` : `Drawing with ${count}`;
}
